import { useEffect, useState } from 'react'
import {
  CloudUploadIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  RefreshIcon,
} from '@heroicons/react/outline'
import { useSyncStatus, SyncStatus } from '../../lib/stateSync'

const formatTimeAgo = (date: Date | null): string => {
  if (!date) return ''
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return `${seconds}mp`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}p`
  const hours = Math.floor(minutes / 60)
  return `${hours}ó`
}

const formatTimeUntil = (date: Date | null): string => {
  if (!date) return ''
  const seconds = Math.floor((date.getTime() - Date.now()) / 1000)
  if (seconds <= 0) return 'most'
  if (seconds < 60) return `${seconds}mp`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}p`
}

const getStatusIcon = (status: SyncStatus, hasPending: boolean) => {
  switch (status) {
    case 'syncing':
      return <RefreshIcon className="w-4 h-4 animate-spin" />
    case 'synced':
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />
    case 'error':
      return <ExclamationCircleIcon className="w-4 h-4 text-red-500" />
    case 'pending':
      return <CloudUploadIcon className="w-4 h-4 text-yellow-500" />
    default:
      return hasPending ? (
        <CloudUploadIcon className="w-4 h-4 text-gray-400" />
      ) : (
        <CheckCircleIcon className="w-4 h-4 text-green-500" />
      )
  }
}

const getStatusText = (
  status: SyncStatus,
  lastSyncedAt: Date | null,
  nextSyncAt: Date | null,
  hasPending: boolean
): string => {
  switch (status) {
    case 'syncing':
      return 'Mentés...'
    case 'synced':
      return lastSyncedAt ? `Mentve ${formatTimeAgo(lastSyncedAt)}` : 'Mentve'
    case 'error':
      return 'Hiba!'
    case 'pending':
      return nextSyncAt
        ? `Mentés ${formatTimeUntil(nextSyncAt)} múlva`
        : 'Várakozás...'
    default:
      return hasPending ? 'Nem mentett' : 'Mentve'
  }
}

export const SyncIndicator = () => {
  const {
    status,
    lastSyncedAt,
    nextSyncAt,
    hasPendingChanges,
    syncNow,
    error,
  } = useSyncStatus()
  const [, setTick] = useState(0)

  // Update every 10 seconds to refresh time displays
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 10000)
    return () => clearInterval(interval)
  }, [])

  const handleClick = async () => {
    if (status === 'syncing') return
    await syncNow()
  }

  const showButton =
    status === 'pending' || status === 'error' || hasPendingChanges

  return (
    <div className="flex items-center gap-1 text-xs">
      <button
        onClick={handleClick}
        disabled={status === 'syncing'}
        className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
          showButton
            ? 'hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer'
            : 'cursor-default'
        } ${
          status === 'error'
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-600 dark:text-gray-400'
        }`}
        title={
          error ||
          getStatusText(status, lastSyncedAt, nextSyncAt, hasPendingChanges)
        }
      >
        {getStatusIcon(status, hasPendingChanges)}
        <span className="hidden sm:inline">
          {getStatusText(status, lastSyncedAt, nextSyncAt, hasPendingChanges)}
        </span>
      </button>
    </div>
  )
}
